import React from 'react'
import { motion } from 'framer-motion'
import { Play, Star } from 'lucide-react'

const AVATARS = [
  { initials: 'TM', from: 'from-zoe-primary', to: 'to-zoe-success' },
  { initials: 'NZ', from: 'from-zoe-accent', to: 'to-zoe-secondary' },
  { initials: 'LK', from: 'from-zoe-success', to: 'to-zoe-primary' },
  { initials: 'SM', from: 'from-zoe-secondary', to: 'to-zoe-accent' },
  { initials: 'JP', from: 'from-zoe-primary', to: 'to-zoe-accent' },
  { initials: 'AN', from: 'from-zoe-accent', to: 'to-zoe-success' },
  { initials: 'DM', from: 'from-zoe-success', to: 'to-zoe-secondary' },
  { initials: 'BN', from: 'from-zoe-secondary', to: 'to-zoe-primary' },
  { initials: 'KS', from: 'from-zoe-primary', to: 'to-zoe-secondary' },
  { initials: 'ZM', from: 'from-zoe-accent', to: 'to-zoe-primary' },
]

const Hero = ({ trackEvent }) => {
  const handleCTAClick = () => {
    trackEvent('hero_cta_click', { button_text: 'Start Your First Prayer' })
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleDemoClick = () => {
    trackEvent('demo_video_click', { location: 'hero' })
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="relative bg-gradient-to-b from-zoe-primary/10 to-white overflow-hidden">
      <div className="container-custom section-padding">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column: Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-heading-dark leading-tight mb-6">
              Live Your Faith,{' '}
              <span className="text-gradient">Not Your Feeds</span>
              <br />
              Prayer That{' '}
              <span className="text-gradient">Truly Lives</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-body-gray mb-8 max-w-2xl mx-auto lg:mx-0">
              ZoeLive Prayer locks distracting apps and guides you into real prayer time.
              Track your spiritual growth with friends who keep you accountable — not scrolling.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <a
                href="#download"
                onClick={handleCTAClick}
                className="btn-primary text-lg inline-flex items-center justify-center"
              >
                Start Your First Prayer (It's Free)
              </a>
              <button
                onClick={handleDemoClick}
                className="text-zoe-primary font-semibold inline-flex items-center justify-center gap-2 hover:underline transition-all"
              >
                <Play size={20} />
                See How It Works
              </button>
            </div>

            {/* Social Proof */}
            <div className="flex flex-col items-center lg:items-start gap-3">
              <div className="flex -space-x-2">
                {AVATARS.map((avatar, i) => (
                  <div
                    key={i}
                    className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatar.from} ${avatar.to} border-2 border-white flex items-center justify-center`}
                  >
                    <span className="text-white text-xs font-bold">{avatar.initials}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm md:text-base text-muted-gray">
                Join <span className="font-semibold text-body-gray">127,000+ believers</span> who've prayed{' '}
                <span className="font-semibold text-body-gray">2.1M minutes</span> this month
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start mt-8">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={16} className="fill-zoe-accent text-zoe-accent" />
                  ))}
                </div>
                <span className="font-semibold">4.8 - 12K Reviews</span>
              </div>
              <div className="text-sm font-semibold text-muted-gray">
                Featured in JOY! Digital
              </div>
            </div>
          </motion.div>

          {/* Right Column: Hero Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Phone Mockup Placeholder */}
            <div className="relative max-w-md mx-auto">
              <div className="bg-gradient-to-br from-zoe-primary to-zoe-success rounded-[3rem] p-4 shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-300">
                <div className="bg-white rounded-[2.5rem] p-6 aspect-[9/19]">
                  {/* Mock Prayer Mode Screen */}
                  <div className="h-full flex flex-col justify-between">
                    <div className="text-center">
                      <div className="text-sm text-muted-gray mb-2">Prayer Mode Active</div>
                      <div className="text-4xl font-bold text-zoe-primary mb-4">8:47</div>
                      <div className="bg-grace-gray rounded-lg p-4 mb-4">
                        <div className="w-full h-32 bg-gradient-to-br from-zoe-primary/20 to-zoe-success/20 rounded-lg mb-2"></div>
                        <div className="text-sm font-semibold text-heading-dark">Morning Peace - Psalm 23</div>
                        <div className="text-xs text-muted-gray">Pastor Thabo Molefe</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-center gap-2">
                        {['Instagram', 'TikTok', 'Facebook'].map((app) => (
                          <div key={app} className="w-12 h-12 rounded-xl bg-muted-gray/20 flex items-center justify-center">
                            <div className="w-6 h-6 bg-muted-gray/30 rounded-lg"></div>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-center text-muted-gray">Apps locked until prayer ends</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Badges */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -top-4 -left-4 bg-white rounded-lg shadow-lg p-3"
              >
                <div className="text-xs font-semibold text-zoe-accent">+1 Day Streak</div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, delay: 0.5, repeat: Infinity }}
                className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-lg p-3"
              >
                <div className="text-xs font-semibold text-zoe-success">Naledi is praying too</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-zoe-primary/5 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-zoe-success/5 rounded-full blur-3xl -z-10"></div>
    </section>
  )
}

export default Hero
